import React from 'react'
import { connect, useDispatch } from 'react-redux'

import { setVideoIsPlaying } from '@store/actionSlices/appConfig'
import {
  BucketSource,
  ProjectIdentity,
  RootStateTypeExtra,
  SessionMap,
} from '@store/types'

import { useIdleTimeHandler } from '@components/idle-time-handler/utils'

import {
  MappingBlockCollection,
  MappingCollection,
} from '@api/interactive-plan'

import { getTabKeyByIndexBy } from '@utilities/floor-tab'
import useEnableCarouselKeyboardControl from '@utilities/useEnableCarouselKeyboardControl'
import VideoPlayerFirebaseService from '@utilities/video-player-firebase-service'
import mapWhiteLabelsWithDefaults from '@utilities/white-label'

import AssetHandler from '@adUtilities/asset-handler/asset-handler'
import { CarouselProvider } from '@adUtilities/components/carousel-provider'
import VideoPlayer from '@adUtilities/components/video-player'
import { VideoPlayerRef } from '@adUtilities/components/video-player/video-player'
import { CarouselControlType } from '@adUtilities/constants/carousel'
import { FileType } from '@adUtilities/constants/common'
import {
  BufferState,
  PlayState,
  SeekState,
  SyncState,
} from '@adUtilities/constants/player'

import GeneralExternalLinkFrame from '../../common/GeneralExternalLinkFrame'
import PropertiesFooterButtons from '../../common/PropertiesFooterButtons'
import useGetFloorPlateGalleries from '../../hooks/useGetFloorPlateGalleries'
import useGetGeneralExternalLinks from '../../hooks/useGetGeneralExternalLinks'
import FloorplanCanvas from './floorplan-canvas'

interface FloorPlateProps {
  getVideoPlayerState: (arg: boolean) => void
  projectIdentity: ProjectIdentity
  session: SessionMap | undefined
  activeLevel: string
  activeBlock: string
  floorPlan: MappingCollection
  blocks: MappingBlockCollection
  setFullScreenToggle: (arg: boolean) => void
  appIdleControls: {
    active: boolean
    duration: number
  }
  effectType: string
  autoPlayIntervalInSeconds: number
}

function FloorplateView({
  getVideoPlayerState,
  projectIdentity,
  session,
  activeLevel,
  activeBlock,
  floorPlan,
  blocks,
  setFullScreenToggle,
  appIdleControls,
  effectType,
  autoPlayIntervalInSeconds,
}: FloorPlateProps) {
  const dispatch = useDispatch()

  const videoPlayerRef = React.useRef<VideoPlayerRef>(null)
  const connectedRef = React.useRef(false)

  const videoPlayerFirebaseService = VideoPlayerFirebaseService({
    galleryName: 'floorGallery',
    projectIdentity,
  })

  const blockKeys: Array<string> = Object.keys(blocks) || []

  const [isConnected, setIsConnected] = React.useState(false)
  const [activeTab, setActiveTab] = React.useState('floor')
  const [videoPlayerState, setVideoPlayerState] = React.useState(false)
  const [currentSlideIndex, setCurrentSlideIndex] = React.useState(0)
  const [videoSyncKey, setVideoSyncKey] = React.useState<number | null>(null)
  const [isExternalLinkFrameOpen, setIsExternalLinkFrameOpen] =
    React.useState(false)

  const enableKeyboardControl = useEnableCarouselKeyboardControl({
    isSessionConnected: isConnected,
    playerState: videoPlayerState,
  })

  const isAppIdle = useIdleTimeHandler({
    isConnected,
    timeoutInSeconds: appIdleControls?.duration || 3,
    isIdleActive: appIdleControls?.active || false,
  })

  const { slides } = useGetFloorPlateGalleries()

  const { generalExternalLink } = useGetGeneralExternalLinks()

  const hasVideoItems: boolean = React.useMemo(
    () => slides.filter((item) => item.fileType === FileType.Video).length > 0,
    [slides]
  )

  const videoSource: string = React.useMemo(() => {
    const item = slides.find(
      (slide, index) =>
        slide.fileType === FileType.Video && index === currentSlideIndex
    )

    if (item?.videoSource) {
      return (
        AssetHandler({
          url: item.videoSource,
          type: BucketSource.Legacy,
          fileType: FileType.Video,
        }) || ''
      )
    }
    return ''
  }, [slides, currentSlideIndex])

  const hasInteractiveMap = (): boolean => {
    let mapName = activeLevel

    if (blockKeys.length > 1) {
      mapName = activeBlock ? `${activeBlock}-${activeLevel}` : activeLevel
    }

    if (mapName in floorPlan) {
      return floorPlan[mapName]?.image !== ''
    }
    return false
  }

  const handleOnPlay = React.useCallback(
    async (args: PlayState) => {
      if (connectedRef.current) {
        await videoPlayerFirebaseService.updateStateOnPlay(args)
      }
    },
    [videoPlayerFirebaseService]
  )

  const handleOnBuffer = React.useCallback(
    async (args: BufferState) => {
      if (connectedRef.current) {
        await videoPlayerFirebaseService.updateStateOnBuffer(args)
      }
    },
    [videoPlayerFirebaseService]
  )

  const handleOnSeek = React.useCallback(
    async (args: SeekState) => {
      if (connectedRef.current) {
        await videoPlayerFirebaseService.updateStateOnSeek(args)
      }
    },
    [videoPlayerFirebaseService]
  )

  const handleOnSync = React.useCallback(
    async (args: SyncState) => {
      if (connectedRef.current) {
        await videoPlayerFirebaseService.updateStateOnSyncRequest(args)
      }
    },
    [videoPlayerFirebaseService]
  )

  const handlePlayerState = React.useCallback(
    (state: boolean) => {
      getVideoPlayerState(state)
      setVideoPlayerState(state)
      dispatch(setVideoIsPlaying(state))
    },
    [getVideoPlayerState]
  )

  React.useEffect(
    () => () => {
      dispatch(setVideoIsPlaying(false))
    },
    []
  )

  React.useEffect(() => setActiveTab('floor'), [activeBlock, activeLevel])

  React.useEffect(() => {
    if (!session) {
      return
    }

    const {
      connected,
      floorGallery: {
        galleryControlV2: {
          activeSlideIndex: activeSlideIndexFirebase,
          activeTabIndex: activeTabIndexFirebase,
        },
        playerControlV2: {
          playerState: playerStateFirebase,
          loop: loopFirebase,
          muted: mutedFirebase,
          volume: volumeFirebase,
          seekToValueInSeconds: seekToValueInSecondsFirebase,
          videoSyncKey: videoSyncKeyFirebase,
        },
      },
      building: { generalExternalLinkPopup },
    } = session
    connectedRef.current = connected

    setIsConnected(connected)
    setCurrentSlideIndex(activeSlideIndexFirebase)
    setActiveTab(getTabKeyByIndexBy(activeTabIndexFirebase || 0))
    setIsExternalLinkFrameOpen(generalExternalLinkPopup)

    videoPlayerRef.current?.updatePlayerState(playerStateFirebase)
    videoPlayerRef.current?.toggleLoop(loopFirebase)
    videoPlayerRef.current?.toggleMute(mutedFirebase)
    videoPlayerRef.current?.updateVolume(volumeFirebase / 10)
    if (seekToValueInSecondsFirebase !== null) {
      videoPlayerRef.current?.seekTo(seekToValueInSecondsFirebase)
    }
    if (videoSyncKeyFirebase !== null) {
      setVideoSyncKey(videoSyncKeyFirebase)
    }
  }, [session?.connected, session?.floorGallery])

  if (!hasInteractiveMap())
    return (
      <div className="flex h-full w-full items-center justify-center bg-white text-4xl">
        No interactive floor plate found for this level.
      </div>
    )

  if (isExternalLinkFrameOpen && generalExternalLink) {
    return (
      <GeneralExternalLinkFrame
        isConnected={isConnected}
        onCloseGeneralExternalLinkFrame={() =>
          setIsExternalLinkFrameOpen(false)
        }
        generalExternalLink={generalExternalLink}
      />
    )
  }

  return (
    <>
      {activeTab === 'gallery' && slides.length > 0 ? (
        <div className="absolute inset-0">
          <CarouselProvider
            gallerySettings={{
              effectType,
              autoPlayIntervalInSeconds,
            }}
            slides={slides}
            activeSlideIndex={currentSlideIndex}
            showControl={!isConnected}
            controlType={CarouselControlType.Thumbnail}
            getCurrentSlideIndex={setCurrentSlideIndex}
            mousewheel={!isConnected}
            isAppIdle={isAppIdle}
            toggleControls={!videoPlayerState}
            enableKeyboardControl={enableKeyboardControl}
          />

          {hasVideoItems && (
            <div
              className={`absolute inset-0 ${
                videoSource ? 'visible' : 'invisible'
              }`}
            >
              <div className="flex h-full w-full items-center justify-center">
                <VideoPlayer
                  ref={videoPlayerRef}
                  sources={[videoSource]}
                  activeIndex={0}
                  getPlayerState={handlePlayerState}
                  showControls={!isConnected}
                  showLoopOption={false}
                  videoSyncKey={videoSyncKey}
                  updateStateOnPlay={handleOnPlay}
                  updateStateOnBuffer={handleOnBuffer}
                  updateStateOnSeek={handleOnSeek}
                  updateStateOnSyncRequest={handleOnSync}
                  hidePlayButton={isConnected}
                />
              </div>
            </div>
          )}
        </div>
      ) : (
        <FloorplanCanvas
          session={session}
          isFullWidth={isAppIdle}
          setFullScreenToggle={setFullScreenToggle}
        />
      )}

      <PropertiesFooterButtons
        activeTab={activeTab}
        onTabChange={(tab) => {
          setActiveTab(tab)
        }}
        isVideoPlaying={videoPlayerState}
        isConnected={isConnected}
        shouldShowGalleryTab={slides.length > 0}
        hasGeneralExternalLink={Boolean(generalExternalLink)}
        onOpenGeneralExternalLinkFrame={() => setIsExternalLinkFrameOpen(true)}
      />
    </>
  )
}

const mapStateToProps = ({
  projectIdentity,
  projectConfig: {
    propertyGalleryTab,
    gallerySettings: { type, intervalInSeconds },
  },
  firestore: { session },
  building: { activeLevel, activeBlock },
  interactivePlan: { floorplan: floorPlan, blocks },
  panoramic,
  whiteLabel,
}: RootStateTypeExtra) => ({
  projectIdentity,
  session,
  activeLevel,
  activeBlock,
  floorPlan,
  blocks,
  panoramic,
  appIdleControls: propertyGalleryTab,
  effectType: type,
  autoPlayIntervalInSeconds: intervalInSeconds,
  panoramicButtonTitle:
    mapWhiteLabelsWithDefaults(whiteLabel).floorStageView.panoramicButtonTitle,
})

export default connect(mapStateToProps)(FloorplateView)
